import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Theater, MessageSquare, Loader2, Calendar, MapPin, CheckCircle2,
  X, Handshake, ArrowRight,
} from "lucide-react";
import TheaterBookingThreadDrawer from "@/components/TheaterBookingThreadDrawer";

const STATUS_TABS = [
  { key: "requested",       label: "New" },
  { key: "counter_offered", label: "Countered" },
  { key: "accepted",        label: "Accepted" },
  { key: "confirmed",       label: "Confirmed" },
  { key: "declined",        label: "Declined" },
  { key: "cancelled",       label: "Cancelled" },
];

const STATUS_TONE = {
  requested:       "bg-amber-500/15 text-amber-300",
  counter_offered: "bg-sky-500/15 text-sky-300",
  accepted:        "bg-emerald-500/15 text-emerald-300",
  confirmed:       "bg-emerald-500/25 text-emerald-200",
  declined:        "bg-red-500/15 text-red-300",
  cancelled:       "bg-white/10 text-white/50",
};

/**
 * Theater Hall's incoming booking requests. Hall can accept, counter,
 * decline, confirm, cancel.
 */
export default function TheaterHallBookings() {
  const [tab, setTab] = useState("requested");
  const [data, setData] = useState({ bookings: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);
  const [actionOn, setActionOn] = useState(null);   // {booking, mode}

  const load = () => {
    setLoading(true);
    api.get("/theater-hall/bookings/mine", { params: { status: tab } })
      .then(({ data }) => setData(data || { bookings: [], counts: {} }))
      .catch(() => setData({ bookings: [], counts: {} }))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tab]);

  const quick = async (id, status, extra = {}) => {
    try {
      await api.patch(`/theater-bookings/${id}/status`, { status, ...extra });
      toast.success(`Marked ${status.replace("_", " ")}.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update.");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="theater-hall-bookings">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">L18 · Booking Requests</div>
      <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-2 text-white">
        Incoming Bookings
      </h1>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="theater-hall-bookings-tabs">
        {STATUS_TABS.map((t) => {
          const count = data.counts?.[t.key] || 0;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`h-9 px-3 rounded-full text-[10px] tracking-[0.22em] uppercase font-semibold transition ${
                active ? "bg-violet-500 text-white" : "bg-white/8 text-white/60 hover:bg-white/15"
              }`}
              data-testid={`theater-hall-bookings-tab-${t.key}`}>
              {t.label}{count > 0 && ` · ${count}`}
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="cs-card rounded-xl p-8 text-center text-white/50">
            <Loader2 size={14} className="animate-spin inline mr-2"/> loading…
          </div>
        ) : data.bookings.length === 0 ? (
          <div className="cs-card rounded-xl p-10 text-center" data-testid="theater-hall-bookings-empty">
            <Calendar size={26} className="mx-auto text-white/25 mb-4"/>
            <div className="text-white/70 text-sm">No bookings in this status.</div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="theater-hall-bookings-list">
            {data.bookings.map((b) => (
              <BookingRow key={b.id} b={b}
                          onOpenThread={() => setDrawer(b)}
                          onAccept={() => quick(b.id, "accepted")}
                          onDecline={() => quick(b.id, "declined")}
                          onConfirm={() => quick(b.id, "confirmed")}
                          onCounter={() => setActionOn({ booking: b, mode: "counter" })}/>
            ))}
          </div>
        )}
      </div>

      {actionOn?.mode === "counter" && (
        <CounterOfferModal
          booking={actionOn.booking}
          onClose={() => setActionOn(null)}
          onSubmit={async (payload) => {
            await quick(actionOn.booking.id, "counter_offered", payload);
            setActionOn(null);
          }}/>
      )}

      <TheaterBookingThreadDrawer
        booking={drawer}
        myRole="hall"
        open={!!drawer}
        onClose={() => { setDrawer(null); load(); }}
        onSent={load}
      />
    </div>
  );
}

function BookingRow({ b, onOpenThread, onAccept, onDecline, onConfirm, onCounter }) {
  const canAccept   = b.status === "requested";
  const canDecline  = b.status === "requested" || b.status === "counter_offered";
  const canCounter  = b.status === "requested";
  const canConfirm  = b.status === "accepted" || b.status === "counter_offered";
  return (
    <div className="cs-card rounded-xl p-5 flex flex-wrap items-start justify-between gap-3"
         data-testid={`theater-hall-booking-${b.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display text-base uppercase tracking-tight text-white">{b.group_name}</span>
          <span className={`px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase ${STATUS_TONE[b.status] || "bg-white/8 text-white/60"}`}>
            {b.status.replace("_", " ")}
          </span>
        </div>
        <div className="mt-1 text-[11px] text-white/55">
          {b.production_title && <span>{b.production_title}</span>}
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-white/70">
          <div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-white/40">Date · Slot</div>
            <div className="mt-0.5">{b.requested_date} · {b.slot}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-white/40">Their Offer</div>
            <div className="mt-0.5">₹{(b.offered_price || 0).toLocaleString()}</div>
          </div>
          {b.counter_price > 0 && (
            <div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-sky-300">Your Counter</div>
              <div className="mt-0.5 text-sky-200">₹{(b.counter_price || 0).toLocaleString()}{b.counter_date && ` on ${b.counter_date}`}</div>
            </div>
          )}
        </div>
        {b.note && (
          <p className="mt-3 text-[11px] text-white/55 leading-relaxed line-clamp-2">"{b.note}"</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 justify-end">
        <Button onClick={onOpenThread}
                className="h-9 rounded-full text-[10px] uppercase tracking-[0.22em] bg-white/8 hover:bg-white/15 text-white"
                data-testid={`theater-hall-booking-thread-${b.id}`}>
          <MessageSquare size={12} className="mr-1.5"/> Thread
        </Button>
        {canAccept && (
          <Button onClick={onAccept}
                  className="h-9 rounded-full text-[10px] uppercase tracking-[0.22em] bg-emerald-500 text-black hover:bg-emerald-400"
                  data-testid={`theater-hall-booking-accept-${b.id}`}>
            <CheckCircle2 size={12} className="mr-1.5"/> Accept
          </Button>
        )}
        {canCounter && (
          <Button onClick={onCounter}
                  className="h-9 rounded-full text-[10px] uppercase tracking-[0.22em] bg-sky-500 text-white hover:bg-sky-400"
                  data-testid={`theater-hall-booking-counter-${b.id}`}>
            <Handshake size={12} className="mr-1.5"/> Counter
          </Button>
        )}
        {canConfirm && (
          <Button onClick={onConfirm}
                  className="h-9 rounded-full text-[10px] uppercase tracking-[0.22em] bg-emerald-500 text-black hover:bg-emerald-400"
                  data-testid={`theater-hall-booking-confirm-${b.id}`}>
            <ArrowRight size={12} className="mr-1.5"/> Confirm
          </Button>
        )}
        {canDecline && (
          <Button onClick={onDecline}
                  className="h-9 rounded-full text-[10px] uppercase tracking-[0.22em] bg-red-500/20 hover:bg-red-500/30 text-red-300"
                  data-testid={`theater-hall-booking-decline-${b.id}`}>
            <X size={12} className="mr-1.5"/> Decline
          </Button>
        )}
      </div>
    </div>
  );
}

function CounterOfferModal({ booking, onClose, onSubmit }) {
  const [form, setForm] = useState({
    counter_price: booking.counter_price || booking.offered_price + 5000,
    counter_date:  booking.counter_date || booking.requested_date,
    note:          "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit({
        counter_price: Number(form.counter_price) || 0,
        counter_date:  form.counter_date,
        note:          form.note,
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
         onClick={onClose} data-testid="theater-counter-modal">
      <div className="w-full max-w-lg cs-card rounded-2xl p-6 sm:p-8"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-sky-500/15 text-sky-400 flex items-center justify-center">
            <Handshake size={18}/>
          </div>
          <div>
            <div className="font-display text-lg uppercase tracking-tight">Counter Offer</div>
            <div className="text-[11px] text-white/55">to {booking.group_name}</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Counter Price (₹)</Label>
            <Input type="number" min={0} step={500} value={form.counter_price} onChange={set("counter_price")}
                   className="bg-black/40 border-white/15 mt-1.5"
                   data-testid="theater-counter-price"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Counter Date</Label>
            <Input type="date" value={form.counter_date} onChange={set("counter_date")}
                   className="bg-black/40 border-white/15 mt-1.5"
                   data-testid="theater-counter-date"/>
          </div>
        </div>
        <div className="mt-4">
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Note</Label>
          <textarea value={form.note} onChange={set("note")} rows={3}
                    placeholder="Explain the change (weekend rate, tech rider, house crew…)"
                    className="w-full bg-black/40 border border-white/15 rounded-md p-2 mt-1.5 text-sm text-white outline-none focus:border-white/30"
                    data-testid="theater-counter-note"/>
        </div>
        <div className="mt-8 flex gap-3">
          <Button variant="ghost"
                  className="h-11 rounded-full text-[11px] uppercase tracking-[0.22em] text-white/70 hover:bg-white/5"
                  onClick={onClose}
                  data-testid="theater-counter-cancel">Cancel</Button>
          <Button className="flex-1 h-11 rounded-full text-[11px] uppercase tracking-[0.22em] bg-sky-500 text-white hover:bg-sky-400 disabled:opacity-50"
                  disabled={busy} onClick={submit}
                  data-testid="theater-counter-submit">
            {busy ? <><Loader2 size={13} className="animate-spin mr-2"/> Sending…</> : <>Send Counter</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
